import { Button, ButtonVariants, ChirpConfigs, CustomClaimValue, Device, Devices, formatBitrate, getDeviceTypeDisplayName, getLogLevelDisplayName, getOperationModeDisplayName, HardwareStatuses, Icons, RadarConfiguration, SimOperators } from "@app/shared";
import { OfflineModeInfo } from "@app/shared/src/components/OfflineModeInfo";
import { TakeTargetPhotosInfo } from "@app/shared/src/components/TakeTargetPhotosInfo";
import copy from "copy-to-clipboard";
import { forwardRef, ReactNode, useImperativeHandle, useRef, useState } from "react";
import { CalibrationReportsDialog, DeviceConfigSection, EditDeviceDetails } from ".";
import { sanitizeDnsLabel } from "../../helpers";
import { useFetch } from "../../hooks";
import { useEditModelDialog } from "../../hooks/useEditModelDialog";
import { AuthorizeView } from "./AuthorizeView";
import { HoverTooltip } from "./HoverTooltip";
import { IconLink } from "./IconLink";
import { TextArea } from "./inputs";
import { LabeledText } from "./LabeledText";
import { Tab, TabContainer } from "./TabContainer";

type DeviceConfigTabViewProps = {
    device: Device | undefined;
    onChange?: (device: Device) => void;
    // Override the default Devices.save call when the edit dialog is submitted.
    // Returning false signals the save was cancelled (e.g. firmware webconfig's
    // network-restart confirm dialog) so onChange is skipped.
    onSave?: (oldDevice: Device | undefined, newDevice: Device) => Promise<void | boolean>;
    onTabChange?: (title: string) => void;
    claimsForRadar?: CustomClaimValue | CustomClaimValue[];
    titleRight?: string | ReactNode;
    children?: ReactNode;//Array<React.ReactElement<TabProps> | typeof TabSpacer>;
};
export interface DeviceConfigMethods {
    openDialog: () => void;
    getTabTitle: () => string;
}

export const DeviceConfigTabView = forwardRef<DeviceConfigMethods, DeviceConfigTabViewProps>(({ device, onChange, onSave, children, titleRight, onTabChange }, ref) => {
    const [statuses] = useFetch(() => (device ? HardwareStatuses.getRecentForDevice(device?.id, 1) : Promise.resolve(undefined)), [device]);
    const [chirpConfigs] = useFetch(() => ChirpConfigs.getAll());
    const [simOperatorInfo] = useFetch(() => device?.simOperator ? SimOperators.getSimOperatorName(device?.simOperator) : Promise.resolve(undefined), [device]);
    const editDialog = useEditModelDialog(Device);
    const activeTab = useRef("General" as DeviceConfigSection);
    const [showCalibrationReports, setShowCalibrationReports] = useState(false);


    useImperativeHandle(ref, () => ({
        openDialog: () => {
            editDialog.show(
                async (newDevice) => {
                    if (onSave) {
                        const result = await onSave(device, newDevice as Device);
                        if (result === false) return;
                    } else {
                        await Devices.save(newDevice as Device);
                    }
                    if (onChange) await onChange(newDevice as Device);
                },
                "Edit Device",
                device
            );
        },
        getTabTitle: () => {
            return activeTab.current;
        }
    }));
    const getChirpConfigDescription = (device: Device | undefined) => {
        if (!device || !device.sensorConfig) return "";
        const radarConfig = device.sensorConfig as RadarConfiguration;
        if (!radarConfig) return "";

        if (!radarConfig.chirpConfig) return "No Config";

        if (!radarConfig.chirpConfigId && radarConfig.chirpConfig.length > 0) {
            return "Custom Config";
        } else {
            radarConfig.chirpConfigPreset = radarConfig.chirpConfigPreset ?? chirpConfigs?.find(x => x.id === radarConfig.chirpConfigId);
        }

        return (
            <>
                {radarConfig.chirpConfigPreset?.name}
                <br />
                <i>{radarConfig.chirpConfigPreset?.description}</i>
            </>
        );
    };
    if (!!!device?.id) { return; }

    function handleTabChange(newId: string | number): void {
        activeTab.current = newId as DeviceConfigSection;
        onTabChange?.(newId as string);
    }

    return (
        <>
            <TabContainer onTabChange={handleTabChange} titleRight={titleRight}>
                <Tab title="General">
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <LabeledText label="Device Name">
                                {device?.name}
                                <IconLink className="ml-3" Icon={Icons.Clipboard} onClick={() => copy(device?.name ?? "")} />
                            </LabeledText>
                            <LabeledText label="mDNS Address">
                                <span className="font-mono">{sanitizeDnsLabel(device?.name)}.local</span>
                                {device?.name !== sanitizeDnsLabel(device?.name) && (
                                    <span className="ml-2 text-xs text-muted-body-text">(name sanitized for DNS)</span>
                                )}
                            </LabeledText>
                            <LabeledText label="Description">
                                {device?.description}
                            </LabeledText>
                            <LabeledText label="Device Key">
                                {device?.deviceKey}
                                <IconLink className="ml-3" Icon={Icons.Clipboard} onClick={() => copy(device?.deviceKey ?? "")} />
                            </LabeledText>
                            <LabeledText label="Device Id">
                                {device?.id}
                                <IconLink className="ml-3" Icon={Icons.Clipboard} onClick={() => copy(device?.id ?? "")} />
                            </LabeledText>
                            {device?.type !== undefined && <LabeledText label="Device Type">{getDeviceTypeDisplayName(device.type)}</LabeledText>}
                            {device?.mode !== undefined && <LabeledText label="Operation Mode">{getOperationModeDisplayName(device?.mode)}</LabeledText>}
                            <LabeledText label="Firmware Version">
                                <span className="inline-flex items-center gap-1.5">
                                    {device?.firmwareVersion}
                                    <HoverTooltip
                                        hoverable={<Icons.Info className="ml-2 text-brand-primary cursor-help" />}
                                    >
                                        This is the current version running on the device. It may differ from the Upgrade tab, which shows the latest version installed on the device.
                                    </HoverTooltip>
                                </span>
                            </LabeledText>
                            <LabeledText label="Hardware Version">{device?.hardwareVersion}</LabeledText>
                            <LabeledText label="Desired Heading">{device?.headingDegrees}°</LabeledText>
                            <LabeledText label="Desired Tilt">
                                {device?.desiredTiltDegrees}°&nbsp;
                                {statuses && statuses.length > 0 && statuses[0].orientation && statuses[0].orientation.error === 0 && <span className="text-muted-body-text">(Actual: {(statuses[0].orientation?.pitch * 0.0572958).toFixed(2)}°)</span>}
                            </LabeledText>
                            <LabeledText label="Height">{device?.height}m</LabeledText>
                            <LabeledText label="Offset from Origin">
                                ({device?.positionX}m, {device?.positionY}m)
                            </LabeledText>
                            <LabeledText label="Max History Per Second">{device?.historyPerSecond ?? "—"}</LabeledText>
                            <LabeledText label="Always Stream Radar Data">{device?.alwaysStreamBinaryData ? "Yes" : "No"}</LabeledText>
                            {device?.alwaysStreamBinaryData && (
                                <>
                                    <LabeledText label="Binary Stream IP">{device?.streamBinaryDestinationIp}</LabeledText>
                                    <LabeledText label="Binary Stream Port">{device?.streamBinaryDestinationPort}</LabeledText>
                                </>
                            )}
                            <LabeledText label="Offline Mode">
                                <span className="inline-flex items-center gap-1.5">
                                    {device?.offlineMode ? 'True' : 'False'}
                                    <OfflineModeInfo />
                                </span>
                            </LabeledText>
                            <LabeledText label="Data Retention">
                                {device?.dataRetentionDays
                                    ? `${device.dataRetentionDays} day${device.dataRetentionDays === 1 ? "" : "s"}`
                                    : "until synced"}
                            </LabeledText>
                            <LabeledText label="Log Retention">
                                {device?.logRetentionDays !== undefined && device?.logRetentionDays !== null
                                    ? `${device.logRetentionDays} day${device.logRetentionDays === 1 ? "" : "s"}`
                                    : "default"}
                            </LabeledText>
                            <LabeledText label="Preserved Log Level">
                                {device?.logRetentionLevel !== undefined && device?.logRetentionLevel !== null
                                    ? getLogLevelDisplayName(device.logRetentionLevel)
                                    : "default"}
                            </LabeledText>
                        </div>
                    </div>
                </Tab>

                <Tab title="Network">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <LabeledText label="Sim ICCID">
                            {device?.simIccid ? (device?.simIccid + " (" + (simOperatorInfo?.operator ?? device?.simOperator) + ")") : "No SIM"}
                        </LabeledText>
                        <LabeledText label="MAC Address">
                            {device?.macAddress}
                            {device?.macAddress && <IconLink className="ml-3" Icon={Icons.Clipboard} onClick={() => copy(device?.macAddress ?? "")} />}
                        </LabeledText>
                        <LabeledText label="DHCP Enabled">{device?.enableDhcp ? "Yes" : "No"}</LabeledText>
                        <LabeledText className="sm:col-start-1" label="IP Address">
                            {device?.ipv4Address}
                            {device?.ipv4Address && <IconLink className="ml-3" Icon={Icons.Clipboard} onClick={() => copy(device?.ipv4Address ?? "")} />}
                        </LabeledText>
                        <LabeledText label="Gateway IPs">{device?.gatewayAddresses}</LabeledText>
                        <LabeledText label="DNS Servers">{device?.dnsServers}</LabeledText>
                        <LabeledText label="NTP Servers">{device?.ntpServers}</LabeledText>
                        <LabeledText label="Heartbeat Frequency">{device?.secondsBetweenHeartbeat}s</LabeledText>
                    </div>
                </Tab>

                <Tab title="Video">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-0">
                            <LabeledText label="DVR Enabled">{device?.enableDvrRecording ? "Yes" : "No"}</LabeledText>
                            <LabeledText label="DVR Bounding Boxes">{device?.dvrBoundingBoxes ? "Yes" : "No"}</LabeledText>
                            <LabeledText label="DVR Bitrate">{formatBitrate(device?.dvrBitrateBps)}</LabeledText>
                            <LabeledText label="DVR Resolution">{device?.dvrResolutionX + "x" + device?.dvrResolutionY}</LabeledText>
                            <LabeledText label="DVR Frame Rate">{device?.dvrFps + " frames/s"}</LabeledText>
                            <LabeledText label="DVR Retention Period">{device?.dvrRetentionHours ? `${device?.dvrRetentionHours} hour${device?.dvrRetentionHours > 1 ? "(s)" : ""}` : "none"}</LabeledText>
                        </div>

                        <div className="p-0">
                            <LabeledText label="Streaming Enabled">{device?.alwaysStreamVideo ? "Yes" : "No"}</LabeledText>
                            <LabeledText label="Streaming Bounding Boxes">{device?.videoBoundingBoxes ? "Yes" : "No"}</LabeledText>
                            <LabeledText label="Streaming Bitrate">{formatBitrate(device?.videoBitrateBps)}</LabeledText>
                            <LabeledText label="Streaming Resolution">{device?.videoResolutionX + "x" + device?.videoResolutionY}</LabeledText>
                            <LabeledText label="Streaming Frame Rate">{device?.videoFps + " frames/s"}</LabeledText>
                        </div>
                    </div>
                </Tab>
                <Tab title="Target Photos">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-0">
                            <LabeledText label="Photograph Every Target">
                                <span className="inline-flex items-center gap-1.5">
                                    {device?.takeTargetPhotos ? "true" : "false"}
                                    <TakeTargetPhotosInfo />
                                </span>
                            </LabeledText>
                            <LabeledText label="Resolution">{device?.snapshotResolutionX + "x" + device?.snapshotResolutionY}</LabeledText>
                            <LabeledText label="Seconds between Photos">{device?.minSecondsBetweenPhotos}</LabeledText>
                            <LabeledText label="Device Photo Refresh">
                                {device?.devicePhotoUpdateIntervalMinutes == null
                                    ? "Every 120 min (default)"
                                    : device.devicePhotoUpdateIntervalMinutes <= 0
                                        ? "Off"
                                        : `Every ${device.devicePhotoUpdateIntervalMinutes} min`}
                            </LabeledText>
                        </div>

                        <div className="p-0">
                            <LabeledText label="PNG Format">{device?.photoPNG ? "true" : "false"}</LabeledText>
                            {!!!(device.photoPNG) && <LabeledText label="JPG Quality">{device.jpgQuality}</LabeledText>}
                            <LabeledText label="Photo Retention">
                                {device?.targetPhotoRetentionDays
                                    ? `${device.targetPhotoRetentionDays} day${device.targetPhotoRetentionDays === 1 ? "" : "s"}`
                                    : "until synced"}
                            </LabeledText>
                        </div>
                    </div>
                </Tab>
                <Tab title="Radar">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <LabeledText label="Enabled">{(device?.sensorConfig as RadarConfiguration)?.transmitterEnabled ? "true" : "false"}</LabeledText>
                        <LabeledText label="Calibrated">{((device?.sensorConfig as RadarConfiguration)?.calibrationConfig?.length ?? 0) > 0 ? "true" : "false"}</LabeledText>
                        <LabeledText label="Seconds to Transmit per Minute">{(device?.sensorConfig as RadarConfiguration)?.secondsToTransmitPerMinute}</LabeledText>
                        <LabeledText label="Minimum Seconds to Transmit">{(device?.sensorConfig as RadarConfiguration)?.minimumSecondsToTransmit}</LabeledText>
                        <LabeledText label="CHIRP Config">{getChirpConfigDescription(device)}</LabeledText>
                        <AuthorizeView claim={CustomClaimValue.SuperAdmin}>
                            {/* Manufacturing records — useful to whoever builds and diagnoses the
                                hardware, not to the customer operating it. */}
                            <LabeledText label="Calibration History">
                                <Button
                                    variant={ButtonVariants.Secondary}
                                    disabled={!device?.id}
                                    onClick={() => setShowCalibrationReports(true)}
                                >
                                    View Calibration Reports
                                </Button>
                            </LabeledText>
                            <LabeledText className="sm:col-span-2" label="CHIRP Config">
                                <TextArea disabled rows={10} value={(device?.sensorConfig as RadarConfiguration)?.chirpConfig} />
                            </LabeledText>
                        </AuthorizeView>
                    </div>
                </Tab>
                {children as any}
            </TabContainer>

            {editDialog.renderModal((model, helpers, onSubmit, onCancel) => (
                <EditDeviceDetails initialSection={activeTab.current} onSubmit={onSubmit} onCancel={onCancel} device={model} helpers={helpers} />
            ), "w-full max-w-5xl rounded-2xl")}

            <CalibrationReportsDialog
                show={showCalibrationReports}
                deviceId={device?.id}
                onClose={() => setShowCalibrationReports(false)}
            />
        </>
    );
});
